// Client fetch tipizzato verso /api/* (ARCHITECTURE.md §6, DATA_MODEL.md §4).
import type {
  AlertsResponse,
  MemberCreateInput,
  MemberDTO,
  ShareCreateInput,
  ShareResponse,
  DocumentCreateInput,
  DocumentDTO,
  DocumentListResponse,
  MeDTO,
  MeUpdateInput,
  SecurityUpdateInput,
  SignedUrlResponse,
  User,
  WarrantyCreateInput,
  WarrantyDTO,
  WarrantyListResponse,
} from '@shared/types';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string) {
    super(message ?? code);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: init?.body && !(init.body instanceof FormData) ? { 'content-type': 'application/json' } : undefined,
    ...init,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data.error ?? 'error', data.message);
  return data as T;
}

export const api = {
  // --- auth ---
  me: () => request<MeDTO>('/me'),
  login: (email: string, password: string) =>
    request<User>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  register: (email: string, password: string, display_name?: string) =>
    request<User>('/auth/register', { method: 'POST', body: JSON.stringify({ email, password, display_name }) }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  // --- account (Modifica profilo §6.11 / Sicurezza §6.10) ---
  updateMe: (input: MeUpdateInput) =>
    request<MeDTO>('/me', { method: 'PATCH', body: JSON.stringify(input) }),
  updateSecurity: (input: SecurityUpdateInput) =>
    request<MeDTO>('/me/security', { method: 'PATCH', body: JSON.stringify(input) }),
  setPin: (new_pin: string, current_pin?: string) =>
    request<{ ok: boolean }>('/me/pin', { method: 'POST', body: JSON.stringify({ new_pin, current_pin }) }),
  verifyPin: (pin: string) =>
    request<{ ok: boolean }>('/me/pin/verify', { method: 'POST', body: JSON.stringify({ pin }) }),

  // --- documents ---
  listDocuments: (params?: { type?: string; q?: string; member_id?: string }) =>
    request<DocumentListResponse>(`/documents${qs(params)}`),
  getDocument: (id: string) => request<DocumentDTO>(`/documents/${id}`),
  documentFileUrl: (id: string, which: 'front' | 'back' | 'pdf' | 'thumb', disposition: 'inline' | 'attachment' = 'inline') =>
    request<SignedUrlResponse>(`/documents/${id}/files/${which}/url?disposition=${disposition}`),
  createDocument: (input: DocumentCreateInput) =>
    request<DocumentDTO>('/documents', { method: 'POST', body: JSON.stringify(input) }),
  updateDocument: (id: string, input: Partial<DocumentCreateInput>) =>
    request<DocumentDTO>(`/documents/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteDocument: (id: string) => request<void>(`/documents/${id}`, { method: 'DELETE' }),

  // --- warranties ---
  listWarranties: (params?: { status?: string; q?: string; member_id?: string }) =>
    request<WarrantyListResponse>(`/warranties${qs(params)}`),
  getWarranty: (id: string) => request<WarrantyDTO>(`/warranties/${id}`),
  attachmentUrl: (warrantyId: string, attId: string, disposition: 'inline' | 'attachment' = 'inline') =>
    request<SignedUrlResponse>(`/warranties/${warrantyId}/attachments/${attId}/url?disposition=${disposition}`),
  createWarranty: (input: WarrantyCreateInput) =>
    request<WarrantyDTO>('/warranties', { method: 'POST', body: JSON.stringify(input) }),
  updateWarranty: (id: string, input: Partial<WarrantyCreateInput>) =>
    request<WarrantyDTO>(`/warranties/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteWarranty: (id: string) => request<void>(`/warranties/${id}`, { method: 'DELETE' }),

  // --- files ---
  uploadFile: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return request<{ file_key: string; filename: string; mime: string; size_bytes: number }>('/files', {
      method: 'POST',
      body: fd,
    });
  },
  signedUrl: (key: string, disposition: 'inline' | 'attachment' = 'inline') =>
    request<SignedUrlResponse>(`/files/${encodeURIComponent(key)}/url?disposition=${disposition}`),

  // --- alerts (Avvisi di scadenza §6.12) ---
  listAlerts: () => request<AlertsResponse>('/alerts'),

  // --- share (Condivisione sicura §6.13) ---
  createShare: (input: ShareCreateInput) =>
    request<ShareResponse>('/share', { method: 'POST', body: JSON.stringify(input) }),
  revokeShare: (id: string) => request<void>(`/share/${id}`, { method: 'DELETE' }),

  // --- members (Familiari §6.15) ---
  listMembers: () => request<{ items: MemberDTO[] }>('/members'),
  createMember: (input: MemberCreateInput) =>
    request<MemberDTO>('/members', { method: 'POST', body: JSON.stringify(input) }),

  // --- push ---
  pushConfig: () => request<{ vapid_public_key: string | null }>('/push/config'),
  pushSubscribe: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<{ ok: boolean }>('/push/subscribe', { method: 'POST', body: JSON.stringify(sub) }),
  pushUnsubscribe: (endpoint: string) =>
    request<void>('/push/subscribe', { method: 'DELETE', body: JSON.stringify({ endpoint }) }),
  pushTest: () => request<{ sent: number; subscriptions: number }>('/push/test', { method: 'POST' }),
};

function qs(params?: Record<string, string | undefined>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return '';
  return '?' + new URLSearchParams(entries as [string, string][]).toString();
}
